import { ApplicationTooltip } from "../../../views";
import { NumericField } from "../../../controls";
import { numberFormatters } from "../../../../shared/utils/numberFormatters";
import { assetService } from "../../../../../shared/services/AssetService";
import { ControlGroup } from "../views/ControlGroup";
import { Button, Stack } from "@mui/material";
import { useAppContext } from "../../../../../shared/contexts/app-context/components/AppContext";
import { Dayjs } from "dayjs";
import { ContractDetailsState } from "./ContractDetails";
import { oderManagerUtils } from "../utils/OrderManagerUtils";
import { OrderDetailsState } from "./OrderDetails";
import { ApplicationDateTimePicker } from "../../../../../shared/components/date-time-picker/ApplicationDateTimePicker";

export type PricingDetailsState = {
    dateForPricing: Dayjs;
    impliedVolatilityForPricing: number;
    underlyingPriceForPricing: number;
    theoreticalPrice: number;
    delta: number;
    gamma: number;
    vega: number;
    theta: number;
};

type PricingDetailsProps = {
    contractDetails: ContractDetailsState;
    orderDetails: OrderDetailsState;
    pricingDetails: PricingDetailsState;
    handlePricingDetailsChange: (patch: Partial<PricingDetailsState>) => void;
}

export function PricingParameters({
    contractDetails,
    orderDetails,
    pricingDetails,
    handlePricingDetailsChange,
}: PricingDetailsProps) {
    const {
        marketDataDto,
    } = useAppContext();
    
    const handleCustomOpenedAtChange = (newValue: Dayjs | null) => {
        if (newValue === null){
            return;
        }

        oderManagerUtils.updatePricingParameters(marketDataDto!, contractDetails,
            newValue,
            pricingDetails.impliedVolatilityForPricing,
            pricingDetails.underlyingPriceForPricing,
            handlePricingDetailsChange);
    }

    const handleImpliedVolatilityChange = (newValue: number): void => {
        oderManagerUtils.updatePricingParameters(marketDataDto!, contractDetails,
            pricingDetails.dateForPricing,
            newValue,
            pricingDetails.underlyingPriceForPricing,
            handlePricingDetailsChange);
    }

    const handleCustomUnderlyingPriceChange = (newValue: number): void => {
        oderManagerUtils.updatePricingParameters(marketDataDto!, contractDetails,
            pricingDetails.dateForPricing,
            pricingDetails.impliedVolatilityForPricing,
            newValue,
            handlePricingDetailsChange);
    }

    const handleUseCurrentMarketValuesClick = () => {
        oderManagerUtils.updatePricingParametersWithCurrentMarketValues(
            marketDataDto!, contractDetails, handlePricingDetailsChange);
    }

    const optionsParametersColour = "rgb(156,109,0)";
    
    return (
        <ControlGroup title="Pricing parameters">
            <Stack direction="row" spacing={2}>
                <ApplicationDateTimePicker
                    label="Target date"
                    value={pricingDetails.dateForPricing}
                    onChange={handleCustomOpenedAtChange}
                    tooltipTitle="Date and time for option price calculation"
                    tooltipPlacement={'top-start'}
                    disabled={!assetService.isOptionType(
                        contractDetails.assetType) ||
                        oderManagerUtils.isOriginalOrderArchived(orderDetails)}
                />
                <ApplicationTooltip
                    title="Implied volatility value for option price calculation"
                    placement={'top-start'}
                    disableInteractive
                    offset={[0, -8]}
                >
                <span>
                    <NumericField
                        id="impliedVolatility"
                        name="impliedVolatility"
                        label="IV"
                        value={pricingDetails.impliedVolatilityForPricing}
                        onValueChange={handleImpliedVolatilityChange}
                        formatter={(value: number) => numberFormatters.format4(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                        disabled={!assetService.isOptionType(
                            contractDetails.assetType) ||
                            oderManagerUtils.isOriginalOrderArchived(orderDetails)}
                        width={70}
                        step={0.0001}
                    />
                </span>
                </ApplicationTooltip>
                <ApplicationTooltip
                    title="Underlying asset price for option price calculation"
                    placement={'top-start'}
                    disableInteractive
                    offset={[0, -8]}
                >
                <span>
                    <NumericField
                        id="underlyingPriceAtOpen"
                        name="underlyingPriceAtOpen"
                        label="Underlying price"
                        value={pricingDetails.underlyingPriceForPricing}
                        onValueChange={handleCustomUnderlyingPriceChange}
                        formatter={(value: number) => numberFormatters.format0(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                        disabled={!assetService.isOptionType(
                            contractDetails.assetType) ||
                            oderManagerUtils.isOriginalOrderArchived(orderDetails)}
                    />
                </span>
                </ApplicationTooltip>
            </Stack>
            <Stack direction="row" spacing={2}>
                <NumericField
                    id="markPrice"
                    name="markPrice"
                    label="Mark price"
                    value={pricingDetails.theoreticalPrice}
                    formatter={(value: number) => numberFormatters.format0(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                    width={65}
                    disabled
                    disabledColour={optionsParametersColour}
                />
                <NumericField
                    id="delta"
                    name="delta"
                    label="Delta"
                    value={pricingDetails.delta}
                    formatter={(value: number) => numberFormatters.format2(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                    width={65}
                    disabled
                    disabledColour={optionsParametersColour}
                />
                <NumericField
                    id="gamma"
                    name="gamma"
                    label="Gamma"
                    value={pricingDetails.gamma}
                    formatter={(value: number) => numberFormatters.format6(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                    width={85}
                    disabled
                    disabledColour={optionsParametersColour}
                />
                <NumericField
                    id="vega"
                    name="vega"
                    label="Vega"
                    value={pricingDetails.vega}
                    formatter={(value: number) => numberFormatters.format2(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                    width={65}
                    disabled
                    disabledColour={optionsParametersColour}
                />
                <NumericField
                    id="theta"
                    name="theta"
                    label="Theta"
                    value={pricingDetails.theta}
                    formatter={(value: number) => numberFormatters.format2(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                    width={70}
                    disabled
                    disabledColour={optionsParametersColour}
                />
            </Stack>
            <Button
                size="small"
                variant="contained"
                onClick={handleUseCurrentMarketValuesClick}
                sx={{
                    backgroundColor: theme => theme.palette.primary.light,
                    "&:hover": {
                        backgroundColor: theme => theme.palette.primary.main,
                    },
                }}
                disabled={oderManagerUtils.isOriginalOrderArchived(orderDetails)}
            >
                Use current market values
            </Button>
        </ControlGroup>
    )
}
